import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

/**
 * Env vars validadas con Zod al build-time y runtime.
 * Si falta una requerida o el tipo no coincide, el build falla explícitamente.
 *
 * Política NQORO:
 * - Secretos server-side en `server`
 * - Expuestos al browser en `client` (deben empezar con NEXT_PUBLIC_)
 * - Ver docs/runbooks/accounts-setup.md para saber dónde obtener cada clave
 */
export const env = createEnv({
  server: {
    // ---------- Core ----------
    NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),

    // ---------- Database (Neon Postgres) ----------
    DATABASE_URL: z.string().url().describe('Postgres connection string Neon'),
    DATABASE_URL_UNPOOLED: z.string().url().optional().describe('Direct URL para migrations'),

    // ---------- Auth.js v5 ----------
    AUTH_SECRET: z.string().min(32).describe('openssl rand -base64 32'),
    AUTH_URL: z.string().url().optional(),
    AUTH_TRUST_HOST: z.string().optional(),
    GOOGLE_CLIENT_ID: z.string().min(1).optional(),
    GOOGLE_CLIENT_SECRET: z.string().min(1).optional(),

    // ---------- Stripe ----------
    STRIPE_SECRET_KEY: z.string().startsWith('sk_'),
    STRIPE_WEBHOOK_SECRET: z.string().startsWith('whsec_').optional(),

    // ---------- Mercado Pago ----------
    MERCADOPAGO_ACCESS_TOKEN: z.string().min(1).optional(),
    MERCADOPAGO_WEBHOOK_SECRET: z.string().min(1).optional(),

    // ---------- Skydropx ----------
    SKYDROPX_CLIENT_ID: z.string().min(1).optional(),
    SKYDROPX_CLIENT_SECRET: z.string().min(1).optional(),
    SKYDROPX_API_URL: z.string().url().default('https://pro.skydropx.com/api/v1'),
    SKYDROPX_WEBHOOK_TOKEN: z.string().min(16).optional(),

    // ---------- Email: SMTP cPanel (transaccionales) + Resend (broadcasts) ----------
    SMTP_HOST: z.string().optional(),
    SMTP_PORT: z.coerce.number().optional(),
    SMTP_USER: z.string().optional(),
    SMTP_PASSWORD: z.string().optional(),
    // Acepta tanto "email" como "Display Name <email>"
    SMTP_FROM: z.string().regex(/@/, 'Debe incluir @').optional(),
    RESEND_API_KEY: z.string().startsWith('re_').optional(),
    RESEND_FROM_EMAIL: z.string().email().optional(),
    RESEND_AUDIENCE_ID: z.string().optional(),

    // ---------- Cloudinary ----------
    CLOUDINARY_CLOUD_NAME: z.string().min(1).optional(),
    CLOUDINARY_API_KEY: z.string().min(1).optional(),
    CLOUDINARY_API_SECRET: z.string().min(1).optional(),

    // ---------- Google Gemini (chatbot — FASE 23) ----------
    // Gemini API key from Google AI Studio (free tier: 15 RPM / 1M TPM / 1500 RPD).
    GEMINI_API_KEY: z.string().min(1).optional(),
    GEMINI_MODEL_DEFAULT: z.string().default('gemini-2.0-flash'),
    GEMINI_MODEL_ADVANCED: z.string().default('gemini-2.5-flash'),

    // ---------- Upstash Redis (rate limit + cache) ----------
    UPSTASH_REDIS_REST_URL: z.string().url().optional(),
    UPSTASH_REDIS_REST_TOKEN: z.string().optional(),

    // ---------- Sentry ----------
    SENTRY_ORG: z.string().optional(),
    SENTRY_PROJECT: z.string().optional(),
    SENTRY_AUTH_TOKEN: z.string().optional(),

    // ---------- Analytics server-side (CAPI / Events API) ----------
    /** Meta Conversions API — server-side Purchase/Lead events. */
    META_CAPI_ACCESS_TOKEN: z.string().optional(),
    /** Meta test_event_code para validar en Events Manager antes de prod. */
    META_CAPI_TEST_EVENT_CODE: z.string().optional(),
    /** TikTok Events API — server-side. */
    TIKTOK_EVENTS_API_ACCESS_TOKEN: z.string().optional(),
    TIKTOK_EVENTS_API_TEST_EVENT_CODE: z.string().optional(),
    /** PostHog server-side (distinct key opcional; por default reusa el public). */
    POSTHOG_SERVER_KEY: z.string().optional(),
    /** PostHog Personal API Key (phx_xxx) con scope query:read — necesario para queries server-side. */
    POSTHOG_PERSONAL_API_KEY: z.string().startsWith('phx_').optional(),
    /** PostHog Project ID (numérico) usado en /api/projects/{id}/query/. */
    POSTHOG_PROJECT_ID: z.string().regex(/^\d+$/).optional(),

    // ---------- Vercel Cron secret ----------
    CRON_SECRET: z.string().min(16).optional(),

    // ---------- Tarot ----------
    TAROT_DAILY_SALT: z.string().min(16).default('nqoro-tarot-salt-change-me-please-in-production'),
    TAROT_DEEP_LINK_SECRET: z.string().min(32).optional(),

    // ---------- Admin / seguridad ----------
    ADMIN_EMAILS: z
      .string()
      .optional()
      .describe('CSV de emails staff inicial; uno será SUPERADMIN'),
  },
  client: {
    NEXT_PUBLIC_SITE_URL: z.string().url().default('http://localhost:3000'),
    NEXT_PUBLIC_SITE_NAME: z.string().default('NQORO'),
    NEXT_PUBLIC_DEFAULT_LOCALE: z.enum(['es-MX', 'en-US']).default('es-MX'),

    // ---------- Stripe public ----------
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string().startsWith('pk_').optional(),

    // ---------- Mercado Pago public ----------
    NEXT_PUBLIC_MERCADOPAGO_PUBLIC_KEY: z.string().optional(),

    // ---------- Cloudinary ----------
    NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME: z.string().optional(),
    NEXT_PUBLIC_CLOUDINARY_FOLDER: z.string().default('nqoro'),

    // ---------- Analytics (consent-gated) ----------
    NEXT_PUBLIC_GTM_ID: z.string().optional(),
    NEXT_PUBLIC_GA4_ID: z.string().optional(),
    NEXT_PUBLIC_META_PIXEL_ID: z.string().optional(),
    NEXT_PUBLIC_TIKTOK_PIXEL_ID: z.string().optional(),
    NEXT_PUBLIC_POSTHOG_KEY: z.string().optional(),
    NEXT_PUBLIC_POSTHOG_HOST: z.string().url().default('https://us.i.posthog.com'),
    NEXT_PUBLIC_SENTRY_DSN: z.string().optional(),

    // ---------- CMP ----------
    NEXT_PUBLIC_CMP_ENABLED: z
      .string()
      .default('true')
      .transform((v) => v === 'true'),
  },
  runtimeEnv: {
    NODE_ENV: process.env.NODE_ENV,
    DATABASE_URL: process.env.DATABASE_URL,
    DATABASE_URL_UNPOOLED: process.env.DATABASE_URL_UNPOOLED,
    AUTH_SECRET: process.env.AUTH_SECRET,
    AUTH_URL: process.env.AUTH_URL,
    AUTH_TRUST_HOST: process.env.AUTH_TRUST_HOST,
    GOOGLE_CLIENT_ID: process.env.GOOGLE_CLIENT_ID,
    GOOGLE_CLIENT_SECRET: process.env.GOOGLE_CLIENT_SECRET,
    STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
    STRIPE_WEBHOOK_SECRET: process.env.STRIPE_WEBHOOK_SECRET,
    MERCADOPAGO_ACCESS_TOKEN: process.env.MERCADOPAGO_ACCESS_TOKEN,
    MERCADOPAGO_WEBHOOK_SECRET: process.env.MERCADOPAGO_WEBHOOK_SECRET,
    SKYDROPX_CLIENT_ID: process.env.SKYDROPX_CLIENT_ID,
    SKYDROPX_CLIENT_SECRET: process.env.SKYDROPX_CLIENT_SECRET,
    SKYDROPX_API_URL: process.env.SKYDROPX_API_URL,
    SKYDROPX_WEBHOOK_TOKEN: process.env.SKYDROPX_WEBHOOK_TOKEN,
    SMTP_HOST: process.env.SMTP_HOST,
    SMTP_PORT: process.env.SMTP_PORT,
    SMTP_USER: process.env.SMTP_USER,
    SMTP_PASSWORD: process.env.SMTP_PASSWORD,
    SMTP_FROM: process.env.SMTP_FROM,
    RESEND_API_KEY: process.env.RESEND_API_KEY,
    RESEND_FROM_EMAIL: process.env.RESEND_FROM_EMAIL,
    RESEND_AUDIENCE_ID: process.env.RESEND_AUDIENCE_ID,
    CLOUDINARY_CLOUD_NAME: process.env.CLOUDINARY_CLOUD_NAME,
    CLOUDINARY_API_KEY: process.env.CLOUDINARY_API_KEY,
    CLOUDINARY_API_SECRET: process.env.CLOUDINARY_API_SECRET,
    GEMINI_API_KEY: process.env.GEMINI_API_KEY,
    GEMINI_MODEL_DEFAULT: process.env.GEMINI_MODEL_DEFAULT,
    GEMINI_MODEL_ADVANCED: process.env.GEMINI_MODEL_ADVANCED,
    UPSTASH_REDIS_REST_URL: process.env.UPSTASH_REDIS_REST_URL,
    UPSTASH_REDIS_REST_TOKEN: process.env.UPSTASH_REDIS_REST_TOKEN,
    SENTRY_ORG: process.env.SENTRY_ORG,
    SENTRY_PROJECT: process.env.SENTRY_PROJECT,
    SENTRY_AUTH_TOKEN: process.env.SENTRY_AUTH_TOKEN,
    CRON_SECRET: process.env.CRON_SECRET,
    TAROT_DAILY_SALT: process.env.TAROT_DAILY_SALT,
    TAROT_DEEP_LINK_SECRET: process.env.TAROT_DEEP_LINK_SECRET,
    ADMIN_EMAILS: process.env.ADMIN_EMAILS,

    NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
    NEXT_PUBLIC_SITE_NAME: process.env.NEXT_PUBLIC_SITE_NAME,
    NEXT_PUBLIC_DEFAULT_LOCALE: process.env.NEXT_PUBLIC_DEFAULT_LOCALE,
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
    NEXT_PUBLIC_MERCADOPAGO_PUBLIC_KEY: process.env.NEXT_PUBLIC_MERCADOPAGO_PUBLIC_KEY,
    NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME: process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME,
    NEXT_PUBLIC_CLOUDINARY_FOLDER: process.env.NEXT_PUBLIC_CLOUDINARY_FOLDER,
    NEXT_PUBLIC_GTM_ID: process.env.NEXT_PUBLIC_GTM_ID,
    NEXT_PUBLIC_GA4_ID: process.env.NEXT_PUBLIC_GA4_ID,
    NEXT_PUBLIC_META_PIXEL_ID: process.env.NEXT_PUBLIC_META_PIXEL_ID,
    NEXT_PUBLIC_TIKTOK_PIXEL_ID: process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ID,
    NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
    NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
    META_CAPI_ACCESS_TOKEN: process.env.META_CAPI_ACCESS_TOKEN,
    META_CAPI_TEST_EVENT_CODE: process.env.META_CAPI_TEST_EVENT_CODE,
    TIKTOK_EVENTS_API_ACCESS_TOKEN: process.env.TIKTOK_EVENTS_API_ACCESS_TOKEN,
    TIKTOK_EVENTS_API_TEST_EVENT_CODE: process.env.TIKTOK_EVENTS_API_TEST_EVENT_CODE,
    POSTHOG_SERVER_KEY: process.env.POSTHOG_SERVER_KEY,
    POSTHOG_PERSONAL_API_KEY: process.env.POSTHOG_PERSONAL_API_KEY,
    POSTHOG_PROJECT_ID: process.env.POSTHOG_PROJECT_ID,
    NEXT_PUBLIC_CMP_ENABLED: process.env.NEXT_PUBLIC_CMP_ENABLED,
  },
  emptyStringAsUndefined: true,
  skipValidation: process.env.SKIP_ENV_VALIDATION === 'true',
});
